import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PriceTicker() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/metal-prices`);
        if (!r.ok) throw new Error("bad response");
        const json = await r.json();
        if (alive) {
          setData(json);
          setFailed(false);
        }
      } catch {
        if (alive) setFailed(true);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (failed && !data) {
    return (
      <div data-testid="price-ticker-unavailable" className="border-y border-[#283244] bg-[#0D1119] py-3 text-center font-mono-tech text-xs text-[#64748B] tracking-[0.2em] uppercase">
        Live prices temporarily unavailable
      </div>
    );
  }
  if (!data) {
    return <div data-testid="price-ticker-loading" className="border-y border-[#283244] bg-[#0D1119] py-3 h-[46px]" />;
  }

  const renderItem = (q, keySuffix) => {
    const pct = q.change_pct;
    const up = pct != null && pct > 0;
    const down = pct != null && pct < 0;
    return (
      <span key={`${q.symbol}-${keySuffix}`} className="flex items-center gap-3 px-8 whitespace-nowrap">
        <span className="font-mono-tech text-xs font-semibold tracking-[0.15em] text-[#F4F6F8] uppercase">{q.name}</span>
        <span className="font-mono-tech text-xs text-[#F07D38] font-semibold">${q.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        <span className={`flex items-center gap-1 font-mono-tech text-[11px] ${up ? "text-emerald-400" : down ? "text-red-400" : "text-[#64748B]"}`}>
          {pct == null ? <Minus size={11} /> : up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {pct == null ? "—" : `${up ? "+" : ""}${pct.toFixed(2)}%`}
        </span>
        <span className="w-1.5 h-1.5 rotate-45 bg-[#E06D28]/60 shrink-0" />
      </span>
    );
  };

  return (
    <div data-testid="price-ticker" className="relative border-y border-[#283244] bg-[#0D1119] overflow-hidden" aria-label="Live metal prices">
      <div className="flex items-center">
        <div className="shrink-0 z-10 flex items-center gap-2 pl-4 sm:pl-8 pr-5 py-3 bg-[#0A0D12] border-r border-[#E06D28]/30">
          <span className={`w-1.5 h-1.5 rounded-full ${data.stale ? "bg-amber-500" : "bg-emerald-400"} animate-pulse`} />
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">
            {data.stale ? "Delayed" : "Live"} · USD/oz
          </span>
        </div>
        <div className="overflow-hidden flex-1 hover:[&_.marquee-track]:[animation-play-state:paused]">
          <div className="marquee-track" style={{ animationDuration: "30s" }}>
            <div className="flex items-center shrink-0">{data.quotes.map((q) => renderItem(q, "a"))}</div>
            <div className="flex items-center shrink-0" aria-hidden="true">{data.quotes.map((q) => renderItem(q, "b"))}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
